import { backupCodeCandidates } from "@eca/shared";
import { ShieldCheck } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { AuthLayout } from "@/components/layout/auth-layout";
import { Button } from "@/components/ui/button";
import { Checkbox, Input, StackField } from "@/components/ui/form";
import { Notice } from "@/components/ui/notice";
import { authClient, authErrorMessage } from "@/lib/auth-client";
import { useRefreshMe } from "@/lib/session";
import { safeNext } from "./login";

export function TwoFactorPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const refresh = useRefreshMe();
  const [mode, setMode] = useState<"totp" | "backup">("totp");
  const [code, setCode] = useState("");
  const [trust, setTrust] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);
  const [loading, setLoading] = useState(false);

  /** A backup code as typed, then in upper case (codes made before 2026-10-22 mix cases). */
  const verifyBackup = async () => {
    let last: Awaited<ReturnType<typeof authClient.twoFactor.verifyBackupCode>>["error"] = null;
    for (const candidate of backupCodeCandidates(code)) {
      const { error } = await authClient.twoFactor.verifyBackupCode({ code: candidate, trustDevice: trust });
      if (!error) return null;
      last = error;
      if (error.code !== "INVALID_BACKUP_CODE") break;
    }
    return last;
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const error = mode === "totp" ? (await authClient.twoFactor.verifyTotp({ code: code.normalize("NFKC").replace(/\s/g, ""), trustDevice: trust })).error : await verifyBackup();
    setLoading(false);
    if (error) {
      setExpired(error.code === "INVALID_TWO_FACTOR_COOKIE" || error.code === "ACCOUNT_TEMPORARILY_LOCKED" || error.code === "TOO_MANY_ATTEMPTS_REQUEST_NEW_CODE");
      return setError(authErrorMessage(error));
    }
    await refresh();
    navigate(safeNext(params.get("next")), { replace: true });
  };

  return (
    <AuthLayout title="二段階認証" lead={mode === "totp" ? "認証アプリに表示されている6桁の確認コードを入力してください。" : "保存しておいたバックアップコードを1つ入力してください。"}>
      <form noValidate onSubmit={submit} className="space-y-4">
        {error && (
          <Notice tone="bad">
            {error}
            {expired && (
              <Link to={`/login?next=${encodeURIComponent(safeNext(params.get("next")))}`} className="mt-1 block font-bold text-brand underline-offset-4 hover:underline">
                ログイン画面へ戻る
              </Link>
            )}
          </Notice>
        )}
        <StackField label={mode === "totp" ? "確認コード（6桁）" : "バックアップコード"}>
          {(id) => (
            <Input
              id={id}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputMode={mode === "totp" ? "numeric" : "text"}
              autoComplete="one-time-code"
              maxLength={mode === "totp" ? 7 : 32}
              placeholder={mode === "totp" ? undefined : "XXXXX-XXXXX"}
              autoCapitalize={mode === "totp" ? undefined : "characters"}
              spellCheck={false}
              className="text-center font-mono text-xl"
              autoFocus
            />
          )}
        </StackField>
        <Checkbox label="この端末を30日間信頼する（共用の端末では選ばないでください）" checked={trust} onChange={(e) => setTrust(e.target.checked)} />
        <Button type="submit" size="lg" className="w-full" loading={loading} disabled={code.trim().length < 6}>
          <ShieldCheck />
          確認する
        </Button>
        <button type="button" className="w-full text-center text-[13px] text-brand hover:underline" onClick={() => (setMode(mode === "totp" ? "backup" : "totp"), setCode(""), setError(null))}>
          {mode === "totp" ? "認証アプリが使えない場合（バックアップコードを使う）" : "認証アプリのコードを使う"}
        </button>
      </form>
    </AuthLayout>
  );
}
